import React, { useRef } from 'react';
import { Transition } from 'react-transition-group';
import type { CSSProperties, Component, HTMLAttributes, MutableRefObject } from 'react';
import type { TransitionStatus } from 'react-transition-group';
import type { TransitionProps } from 'react-transition-group/Transition';
import type { CustomTransitionProps } from './types';
import { DEFAULT_TRANSITION_FUNC, getAnimationDurationAsInt } from '../../util/styled';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
interface Props extends CustomTransitionProps, HTMLProps, Partial<Component<TransitionProps>> {}

const Collapse = ({ when, children, ...other }: Props): JSX.Element => {
  const child = useRef() as MutableRefObject<Ref>;
  const child_height = child.current ? child.current.offsetHeight : '0';

  const default_duration = getAnimationDurationAsInt();
  const default_style: CSSProperties = {
    transition: `visibility 1s linear 300ms, height ${default_duration}ms ${DEFAULT_TRANSITION_FUNC}`,
    height: 0,
    minHeight: 0,
    overflow: 'hidden',
    // Not taking room in the DOM
    visibility: 'hidden',
    position: 'absolute',
    width: '100%',
  };

  const transition_styles: Record<TransitionStatus, CSSProperties> = {
    entering: {
      height: `${child_height}px`,
      visibility: 'visible',
      position: 'inherit',
    },
    entered: {
      height: 'auto',
      overflow: 'visible',
      visibility: 'visible',
      position: 'inherit',
    },
    exiting: {
      height: `${child_height}px`,
      position: 'inherit',
    },
    exited: {
      position: 'inherit',
    },
    unmounted: {},
  };

  return (
    <Transition
      in={when}
      timeout={{ appear: default_duration, enter: default_duration, exit: 0 }}
      {...(other as HTMLProps & Component<TransitionProps>)}
    >
      {(state) => (
        <div
          style={{
            ...default_style,
            ...transition_styles[state],
          }}
        >
          <div ref={child}>{children}</div>
        </div>
      )}
    </Transition>
  );
};

Collapse.displayName = 'Collapse';

export default Collapse;
